import type { HopDongDaXoa } from '@qlk/shared';
import { NGAY_GIU_HOP_DONG_DA_XOA } from '@qlk/shared';
import { useState } from 'react';

import {
  DataTable,
  EmptyState,
  HopXacNhan,
  Nut,
  StatusBadge,
  ngayDay,
  type CotBang,
} from '../../../ui/index.js';
import { useHopDongDaXoa, useKhoiPhucHopDong } from '../api.js';

/**
 * Mục "Hợp đồng đã xoá" trong tab Hợp đồng.
 *
 * Xoá hợp đồng là xoá mềm, nên bản đã xoá còn nằm đây và khôi phục được trong
 * `NGAY_GIU_HOP_DONG_DA_XOA` ngày. Quá hạn thì dòng vẫn còn để tra lại, chỉ là
 * không khôi phục được nữa — phải tạo hợp đồng mới.
 *
 * Chỉ hiện cho `networks:admin`; API cũng chặn 403.
 */
export function MucHopDongDaXoa({ partnerId }: { partnerId: string }) {
  const daXoa = useHopDongDaXoa(partnerId);
  const dong = daXoa.data ?? [];

  const cot: CotBang<HopDongDaXoa>[] = [
    {
      key: 'so',
      header: 'Số hợp đồng',
      render: (c) => <span className="so-lieu">{c.contractNo}</span>,
    },
    {
      key: 'tyLe',
      header: 'Network giữ',
      align: 'right',
      render: (c) => <span className="so-lieu">{c.defaultSharePercent.replace('.', ',')}%</span>,
    },
    {
      key: 'hieuLuc',
      header: 'Hiệu lực',
      render: (c) =>
        `${ngayDay(c.startDate)} → ${c.endDate ? ngayDay(c.endDate) : 'không thời hạn'}`,
    },
    { key: 'xoaLuc', header: 'Xoá lúc', render: (c) => ngayDay(c.deletedAt) },
    { key: 'nguoiXoa', header: 'Người xoá', render: (c) => c.nguoiXoa ?? '—' },
    { key: 'lyDo', header: 'Lý do', render: (c) => c.lyDoXoa ?? '—' },
    {
      key: 'han',
      header: 'Khôi phục',
      render: (c) =>
        c.conKhoiPhucDuoc ? (
          <StatusBadge tone="warn">đến {ngayDay(c.khoiPhucDenNgay)}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">hết hạn</StatusBadge>
        ),
    },
    {
      key: 'nut',
      header: '',
      align: 'right',
      render: (c) => <NutKhoiPhuc hopDong={c} />,
    },
  ];

  if (!daXoa.isPending && dong.length === 0) return null;

  return (
    <section className="mt-6">
      <h2 className="mb-1 text-tieu-de-khoi font-semibold text-chu">Hợp đồng đã xoá</h2>
      <p className="mb-3 text-phu text-chu-mo">
        Hợp đồng xoá mềm: không vào danh sách và không vào phép tính nào, khôi phục được trong{' '}
        {NGAY_GIU_HOP_DONG_DA_XOA} ngày. Kênh bị **tách** lúc xoá thì khôi phục sẽ hỏi có gắn lại
        hay không; kênh đã chuyển sang hợp đồng khác thì không đụng tới.
      </p>

      <DataTable
        caption="Hợp đồng đã xoá của đối tác"
        columns={cot}
        rows={dong}
        keyOf={(c) => c.id}
        dangTai={daXoa.isPending}
        khiRong={<EmptyState title="Chưa xoá hợp đồng nào" />}
      />

      {daXoa.error && (
        <p role="alert" className="mt-2 text-phu text-bad-chu">
          {daXoa.error.message}
        </p>
      )}
    </section>
  );
}

function NutKhoiPhuc({ hopDong }: { hopDong: HopDongDaXoa }) {
  const khoiPhuc = useKhoiPhucHopDong(hopDong.id);
  const [hoi, datHoi] = useState(false);

  const soKenh = hopDong.soKenhGanLaiDuoc;

  if (!hopDong.conKhoiPhucDuoc) {
    return <span className="text-phu text-chu-mo">Quá {NGAY_GIU_HOP_DONG_DA_XOA} ngày</span>;
  }

  function khoiPhucVoi(ganLaiKenh: boolean): void {
    khoiPhuc.mutate(
      { ganLaiKenh },
      {
        onSuccess: () => {
          datHoi(false);
        },
      },
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Nut
        kieu="phu"
        dangChay={khoiPhuc.isPending}
        onClick={() => {
          /* Không có kênh nào tách rời thì khỏi hỏi. */
          if (soKenh === 0) khoiPhucVoi(false);
          else datHoi(true);
        }}
      >
        Khôi phục
      </Nut>

      {hoi && (
        <HopXacNhan
          title={`Gắn lại ${String(soKenh)} kênh đã tách khi xoá?`}
          nhanXacNhan={`Khôi phục và gắn lại ${String(soKenh)} kênh`}
          dangChay={khoiPhuc.isPending}
          onXacNhan={() => {
            khoiPhucVoi(true);
          }}
          onDong={() => {
            datHoi(false);
            khoiPhuc.reset();
          }}
          loi={khoiPhuc.error?.message ?? null}
        >
          <p className="text-co-ban text-chu-phu">
            Lúc xoá hợp đồng {hopDong.contractNo}, {soKenh} kênh được giữ link với tỷ lệ riêng nên
            bị tách khỏi hợp đồng. Gắn lại thì tab Hợp đồng đếm đúng số kênh và luật chặn sửa/xoá có
            hiệu lực trở lại.
          </p>
          <div className="mt-3">
            <Nut
              kieu="phu"
              dangChay={khoiPhuc.isPending}
              onClick={() => {
                khoiPhucVoi(false);
              }}
            >
              Khôi phục, không gắn kênh
            </Nut>
          </div>
        </HopXacNhan>
      )}

      {khoiPhuc.error && !hoi && (
        <p role="alert" className="text-right text-phu text-bad-chu">
          {khoiPhuc.error.message}
        </p>
      )}
    </div>
  );
}
